import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Trash2, Edit, Pin } from 'lucide-react';
import { Tooltip } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

interface ConversationMenuProps {
  onDelete: () => void;
  onPin: () => void;
  onRename: () => void;
  isPinned?: boolean;
  isPinDisabled?: boolean;
  pinLimitMessage?: string;
  align?: 'left' | 'right';
}

export const ConversationMenu: React.FC<ConversationMenuProps> = ({
  onDelete,
  onPin,
  onRename,
  isPinned = false,
  isPinDisabled = false,
  pinLimitMessage = 'Pin limit reached',
  align = 'right',
}) => {
  const { t } = useTranslation('pleri/chat');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete();
    setIsOpen(false);
  };

  const handlePin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPin();
    setIsOpen(false);
  };

  const handleRename = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRename();
    setIsOpen(false);
  };

  return (
    <div className="relative z-10">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex h-5 w-5 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-600 dark:hover:text-gray-400"
        data-testid="conversation-menu-button"
        aria-label={t('moreOptions')}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} top-full z-[10000] mt-1 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {isPinDisabled ? (
            <Tooltip label={pinLimitMessage} placement="left">
              <button
                disabled
                data-testid="conversation-menu-pin-button"
                className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-left text-sm text-gray-400 dark:text-gray-500"
              >
                <Pin className="h-4 w-4" />
                <span>{t('pin')}</span>
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={handlePin}
              data-testid="conversation-menu-pin-button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <Pin className="h-4 w-4" />
              <span>{isPinned ? t('unpin') : t('pin')}</span>
            </button>
          )}
          <button
            onClick={handleRename}
            data-testid="conversation-menu-rename-button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Edit className="h-4 w-4" />
            <span>{t('rename')}</span>
          </button>
          <button
            onClick={handleDelete}
            data-testid="conversation-menu-delete-button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Trash2 className="h-4 w-4" />
            <span>{t('common:delete')}</span>
          </button>
        </div>
      )}
    </div>
  );
};
